// =============================================================================
// SendToProviderModal — confirm sending SELF lines to Inkteo
// -----------------------------------------------------------------------------
// Used by order detail (single) and the order list bulk bar. The operator
// must see store, SELF line count, SHIPPING vs PICKUP, and the no-charge
// line before the irreversible handoff.
// =============================================================================

import { Modal, ModalFooter, Button } from '@tkpod/ui';

export type InkteoFulfillmentChoice = 'SHIPPING' | 'PICKUP';
export type ProductionTypeChoice = 'REGULAR' | 'RUSH' | 'DETECTED';

export interface SendToProviderModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  confirmLabel: string;
  storeName: string;
  shopDomain?: string | null;
  selfLineCount: number;
  orderCount?: number;
  skipCount?: number;
  mixedOrder?: boolean;
  fulfillmentType: InkteoFulfillmentChoice;
  onFulfillmentTypeChange: (value: InkteoFulfillmentChoice) => void;
  productionType: ProductionTypeChoice;
  onProductionTypeChange: (value: ProductionTypeChoice) => void;
  /** Bulk send. DETECTED keeps each order's tag-resolved type. */
  allowDetectedProduction?: boolean;
  productionNote?: string | null;
  checkoutSuggested?: InkteoFulfillmentChoice | null;
  checkoutLabel?: string | null;
  mixedCheckoutNote?: string | null;
  /** Already claimed — operator is correcting PICKUP↔SHIPPING for the next pull. */
  correctingType?: boolean;
  /** Single-order PICKUP preview. Absent on the bulk bar. */
  shipToPreview?: {
    loading: boolean;
    sourceLabel: string | null;
    summary: string | null;
    recipient: string | null;
    error: string | null;
  } | null;
  /** Bulk PICKUP: no per-order preview, explain the fallback. */
  pickupFallbackNote?: string | null;
  onConfirm: () => void;
  loading?: boolean;
  error?: string | null;
}

export function SendToProviderModal({
  open,
  onClose,
  title,
  confirmLabel,
  storeName,
  shopDomain,
  selfLineCount,
  orderCount,
  skipCount,
  mixedOrder,
  fulfillmentType,
  onFulfillmentTypeChange,
  productionType,
  onProductionTypeChange,
  allowDetectedProduction = false,
  productionNote,
  checkoutSuggested,
  checkoutLabel,
  mixedCheckoutNote,
  correctingType,
  shipToPreview,
  pickupFallbackNote,
  onConfirm,
  loading,
  error,
}: SendToProviderModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="lg"
      description={
        correctingType
          ? 'This updates the fulfillment type on the provider submission. The next payload they pull will use the type you choose.'
          : 'This hands production of self-fulfilled lines to the provider. It cannot be undone from this screen.'
      }
    >
      <div className="space-y-4">
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-text-secondary">Store</dt>
            <dd className="text-right font-medium text-text-primary">
              {storeName}
              {shopDomain ? (
                <span className="block text-[11px] font-normal text-text-tertiary">
                  {shopDomain}
                </span>
              ) : null}
            </dd>
          </div>
          {orderCount != null && orderCount > 1 && (
            <div className="flex justify-between gap-4">
              <dt className="text-text-secondary">Orders</dt>
              <dd className="font-medium text-text-primary">{orderCount}</dd>
            </div>
          )}
          <div className="flex justify-between gap-4">
            <dt className="text-text-secondary">Self-fulfilled lines</dt>
            <dd className="font-medium text-text-primary">{selfLineCount}</dd>
          </div>
        </dl>

        {mixedOrder && (
          <p className="rounded-md border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
            Mixed order — only self-fulfilled lines are sent. TK production
            lines stay with their current provider.
          </p>
        )}

        {mixedCheckoutNote && (
          <p className="rounded-md border border-border bg-surface-secondary px-3 py-2 text-xs text-text-secondary">
            {mixedCheckoutNote}
          </p>
        )}

        {skipCount != null && skipCount > 0 && (
          <p className="text-xs text-text-secondary">
            {skipCount} selected {skipCount === 1 ? 'order is' : 'orders are'}{' '}
            ineligible and will be skipped.
          </p>
        )}

        <fieldset className="space-y-2">
          <legend className="text-xs font-medium text-text-primary">
            Fulfillment type
          </legend>
          {checkoutSuggested && (
            <p className="rounded-md border border-border bg-surface-secondary px-3 py-2 text-xs text-text-secondary">
              Shopify checkout:{' '}
              <span className="font-medium text-text-primary">
                {checkoutLabel ??
                  (checkoutSuggested === 'PICKUP'
                    ? 'Pickup in store'
                    : 'Shipping')}
              </span>
              . You can send as shipping or pickup
              {correctingType ? ' on the next payload pull' : ''}.
            </p>
          )}
          {checkoutSuggested && fulfillmentType !== checkoutSuggested && (
            <p className="rounded-md border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
              This differs from the Shopify checkout method. The customer
              notification will follow the type you send.
            </p>
          )}
          <label className="flex items-start gap-2 rounded-md border border-border px-3 py-2 text-sm">
            <input
              type="radio"
              name="fulfillmentType"
              value="SHIPPING"
              checked={fulfillmentType === 'SHIPPING'}
              onChange={() => onFulfillmentTypeChange('SHIPPING')}
              className="mt-0.5"
            />
            <span>
              <span className="font-medium text-text-primary">Shipping</span>
              <span className="block text-[11px] text-text-tertiary">
                Provider buys a label and sends tracking to the customer.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 rounded-md border border-border px-3 py-2 text-sm">
            <input
              type="radio"
              name="fulfillmentType"
              value="PICKUP"
              checked={fulfillmentType === 'PICKUP'}
              onChange={() => onFulfillmentTypeChange('PICKUP')}
              className="mt-0.5"
            />
            <span>
              <span className="font-medium text-text-primary">Pickup</span>
              <span className="block text-[11px] text-text-tertiary">
                Provider produces for collection. Tracking is optional.
              </span>
            </span>
          </label>
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="text-xs font-medium text-text-primary">
            Production type
          </legend>
          {productionNote ? (
            <p className="rounded-md border border-border bg-surface-secondary px-3 py-2 text-xs text-text-secondary">
              {productionNote}
            </p>
          ) : null}
          {allowDetectedProduction ? (
            <label className="flex items-start gap-2 rounded-md border border-border px-3 py-2 text-sm">
              <input
                type="radio"
                name="productionType"
                value="DETECTED"
                checked={productionType === 'DETECTED'}
                onChange={() => onProductionTypeChange('DETECTED')}
                className="mt-0.5"
              />
              <span>
                <span className="font-medium text-text-primary">Detected per order</span>
                <span className="block text-[11px] text-text-tertiary">
                  Rush when the order tag is rush-production. Otherwise Regular.
                </span>
              </span>
            </label>
          ) : null}
          <label className="flex items-start gap-2 rounded-md border border-border px-3 py-2 text-sm">
            <input
              type="radio"
              name="productionType"
              value="REGULAR"
              checked={productionType === 'REGULAR'}
              onChange={() => onProductionTypeChange('REGULAR')}
              className="mt-0.5"
            />
            <span>
              <span className="font-medium text-text-primary">Regular</span>
              <span className="block text-[11px] text-text-tertiary">
                Normal production queue.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 rounded-md border border-border px-3 py-2 text-sm">
            <input
              type="radio"
              name="productionType"
              value="RUSH"
              checked={productionType === 'RUSH'}
              onChange={() => onProductionTypeChange('RUSH')}
              className="mt-0.5"
            />
            <span>
              <span className="font-medium text-text-primary">Rush</span>
              <span className="block text-[11px] text-text-tertiary">
                Move this order ahead in production. The next provider pull uses this choice.
              </span>
            </span>
          </label>
        </fieldset>

        {fulfillmentType === 'PICKUP' && shipToPreview && (
          <div className="rounded-md border border-border bg-surface-secondary px-3 py-2 text-xs text-text-secondary">
            {shipToPreview.loading ? (
              'Resolving pickup ship-to…'
            ) : shipToPreview.error ? (
              <span className="text-red-600">{shipToPreview.error}</span>
            ) : (
              <>
                <span className="font-medium text-text-primary">
                  Ship-to: {shipToPreview.sourceLabel}
                </span>
                {shipToPreview.recipient ? (
                  <span className="mt-1 block">{shipToPreview.recipient}</span>
                ) : null}
                {shipToPreview.summary ? (
                  <span className="mt-0.5 block">{shipToPreview.summary}</span>
                ) : null}
              </>
            )}
          </div>
        )}

        {fulfillmentType === 'PICKUP' && !shipToPreview && pickupFallbackNote && (
          <p className="rounded-md border border-border bg-surface-secondary px-3 py-2 text-xs text-text-secondary">
            {pickupFallbackNote}
          </p>
        )}

        <p className="rounded-md border border-border bg-surface-secondary px-3 py-2 text-xs font-medium text-text-primary">
          No wholesale charge — the merchant is not billed for this handoff.
        </p>

        {error && (
          <p className="rounded-md border border-red-500/20 bg-red-500/10 px-3 py-2 text-xs text-red-600">
            {error}
          </p>
        )}
      </div>
      <ModalFooter>
        <Button variant="outline" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={onConfirm}
          loading={loading}
          disabled={selfLineCount < 1}
        >
          {confirmLabel}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
